// eslint-disable-next-line @exodus/restricted-imports/prefer-basic-utils
import { memoize } from 'lodash';
import { createSelector } from 'reselect';

import multiAssetSourceActivitySelector from '@/ui/flux/tx-log/selectors/multi-asset-source-activity';
import { createDeepEqualOutputSelector } from '@/ui/flux/utils/deep-equals';
import { formatDate, startOfDayTimestampLocal } from '@/utils/datetime';
import type { ActivityItem } from '../types.js';

export const createMultiAssetGroupedWalletActivitySelector = memoize(
  ({ assetNames, walletAccounts, type }) => {
    return createSelector(
      multiAssetSourceActivitySelector({ assetNames, walletAccounts, type }),
      (activity: ActivityItem[]) => {
        const pending: ActivityItem[] = [];
        const byTime = new Map();

        for (const activityItem of activity) {
          if (activityItem.pending) {
            pending.push(activityItem);
          } else {
            const timestamp = startOfDayTimestampLocal(activityItem.date);
            if (byTime.get(timestamp)) {
              byTime.get(timestamp).push(activityItem);
            } else {
              byTime.set(timestamp, [activityItem]);
            }
          }
        }

        return {
          pending,
          byTime,
        };
      },
    );
  },
  ({ assetNames, walletAccounts, type }) =>
    `${assetNames.join(',')}_${walletAccounts.join(',')}_${type}`,
);

const flatGroupedActivityWithTimeLabels = (grouped) => {
  const today = startOfDayTimestampLocal(new Date());
  const yesterday = startOfDayTimestampLocal(new Date(today - 1));

  const activity: ActivityEntry[] = [];

  if (grouped.pending.length > 0) {
    activity.push(
      { id: 'label-unconfirmed', type: 'label', label: 'Pending' },
      ...grouped.pending,
    );
  }

  [...grouped.byTime.entries()]
    .sort((a, b) => (b[0] === a[0] ? 0 : b[0] > a[0] ? 1 : -1))
    .forEach(([key, list]) => {
      const label =
        key === today
          ? 'Today'
          : key === yesterday
            ? 'Yesterday'
            : formatDate(new Date(key));

      activity.push(
        {
          id: `label-${label}`,
          type: 'label',
          label,
        },
        ...list,
      );
    });

  return activity;
};

export type LabelEntry = {
  id: string;
  type: 'label';
  label: string;
};

export type TransactionEntry = ActivityItem;

export type ActivityEntry = LabelEntry | TransactionEntry;

export const createFilteredActivitySelector = memoize(
  ({ assetNames, walletAccounts, type }) => {
    return createDeepEqualOutputSelector(
      createMultiAssetGroupedWalletActivitySelector({
        assetNames,
        walletAccounts,
        type,
      }),
      (grouped) => flatGroupedActivityWithTimeLabels(grouped),
    );
  },
  ({ assetNames, walletAccounts, type }) =>
    `${assetNames.join(',')}_${walletAccounts.join(',')}_${type}`,
);

export default createFilteredActivitySelector;
